<template>
    <div v-if="series" class="space-y-4">
        <FlexibleCard headline-id="documents" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('documents')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('documents')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('documents')"
                    @enter-edit-mode="onEnterEditMode('documents', $t('project-series-documents'))"
                    @leave-edit-mode="leaveEditMode('documents')"
                    @discard-changes="discardChanges('documents')"
                    @save="saveForm('documents')"
                >
                    <CardHeadline :headline-id="headlineId" :help-text="$t('annual-documents-help')">
                        {{ $t('project-series-documents') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <ProjectSeriesDocumentsForm
                        v-if="showForm('documents')"
                        ref="projectSeriesDocumentsFormRef"
                        :series-id="series.id"
                        class="p-5"
                    />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="series.otherDocuments && series.otherDocuments.links?.nodes.length"
                            :label="$t('other-documents')"
                            :items="toCardRowAttachmentLink(series.otherDocuments.links.nodes)"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('other-documents')" text="-" :has-border-bottom="true" />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="systems" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('systems')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('systems')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('systems')"
                    @enter-edit-mode="onEnterEditMode('systems', $t('project-series-systems'))"
                    @leave-edit-mode="leaveEditMode('systems')"
                    @discard-changes="discardChanges('systems')"
                    @save="saveForm('systems')"
                >
                    <CardHeadline :headline-id="headlineId" :help-text="$t('annual-systems-help')">
                        {{ $t('project-series-systems') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <ProjectSeriesSystemsForm
                        v-if="showForm('systems')"
                        ref="projectSeriesSystemsFormRef"
                        :series-id="series.id"
                        class="p-5"
                    />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="series.otherSystems && series.otherSystems.links?.nodes.length"
                            :label="$t('other-systems')"
                            :items="toCardRowAttachmentLink(series.otherSystems.links.nodes)"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('other-systems')" text="-" :has-border-bottom="true" />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="otherLinks" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('otherLinks')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('otherLinks')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('otherLinks')"
                    @enter-edit-mode="onEnterEditMode('otherLinks', $t('project-series-other-links'))"
                    @leave-edit-mode="leaveEditMode('otherLinks')"
                    @discard-changes="discardChanges('otherLinks')"
                    @save="saveForm('otherLinks')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('project-series-other-links') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <ProjectSeriesOtherLinksForm
                        v-if="showForm('otherLinks')"
                        ref="projectSeriesOtherLinksFormRef"
                        :series-id="series.id"
                        class="p-5"
                    />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="series.otherLinks && series.otherLinks.links?.nodes.length"
                            :label="$t('other-links')"
                            :items="toCardRowAttachmentLink(series.otherLinks.links.nodes)"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('other-links')" text="-" :has-border-bottom="true" />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>
    </div>
</template>

<script setup lang="ts">
import CardHeaderEditable from '@/components/Card/CardHeaderEditable.vue';
import CardHeadline from '@/components/Card/CardHeadline.vue';
import CardRowAttachmentGroup from '@/components/Card/CardRowAttachmentGroup.vue';
import CardRowText from '@/components/Card/CardRowText.vue';
import FlexibleCard from '@/components/Card/FlexibleCard.vue';
import { toCardRowAttachmentLink } from '@/components/MultiLink/multilink';
import ContentFormTransition from '@/event/components/ContentFormTransition.vue';
import { useFormRegistry } from '@/event/forms/useFormRegistry';
import { useActiveCard } from '@/forms/composables/useActiveCard';
import { $t } from '@/plugins/fluent';
import { useProjectSeriesInjection } from '@/project/composables/useProjectSeriesInjection';
import ProjectSeriesDocumentsForm from '@/project/forms/series/ProjectSeriesDocumentsForm.vue';
import ProjectSeriesOtherLinksForm from '@/project/forms/series/ProjectSeriesOtherLinksForm.vue';
import ProjectSeriesSystemsForm from '@/project/forms/series/ProjectSeriesSystemsForm.vue';
import { useFluent } from 'fluent-vue';

import { useTemplateRef } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

const fluent = useFluent();
const { series } = useProjectSeriesInjection();

const {
    activeCard,
    resetActiveCard,
    onEnterEditMode,
    showForm,
    editButtonDisabledMessage,
    saveButtonDisabledMessage,
    getCardStatus,
} = useActiveCard();

const projectSeriesDocumentsFormRef = useTemplateRef('projectSeriesDocumentsFormRef');
const projectSeriesSystemsFormRef = useTemplateRef('projectSeriesSystemsFormRef');
const projectSeriesOtherLinksFormRef = useTemplateRef('projectSeriesOtherLinksFormRef');

const { register, saveForm, discardChanges, leaveEditMode, resetAll } = useFormRegistry();

register('documents', projectSeriesDocumentsFormRef);
register('systems', projectSeriesSystemsFormRef);
register('otherLinks', projectSeriesOtherLinksFormRef);

onBeforeRouteLeave(() => {
    if (activeCard.value.id && activeCard.value.status === 'Edit-Touched') {
        if (!confirm(fluent.$t('leave-page-warning'))) {
            return false;
        }
        resetAll();
    }
    resetActiveCard();
    return true;
});
</script>
